/**
 * CDP SDK News Plugin - Event Builders
 * Builds event properties for article tracking events
 */

import type {
  ArticleMetadata,
  ReadingState,
  NewsPluginConfig,
  ArticleViewedProperties,
  ArticleEngagedProperties,
  ArticleReadProperties,
  ArticleReadingSessionProperties,
  ArticleScrollMilestoneProperties,
  ArticleSharedProperties,
  CommentPostedProperties,
  ArticleSavedProperties,
  SearchPerformedProperties,
} from './types';

// =============================================================================
// EVENT NAMES
// =============================================================================

export const NEWS_EVENTS = {
  ARTICLE_VIEWED: 'Article Viewed',
  ARTICLE_ENGAGED: 'Article Engaged',
  ARTICLE_READ: 'Article Read',
  ARTICLE_READING_SESSION: 'Article Reading Session',
  /** Cumulative snapshot of a still-alive reading session, emitted on every
   *  tab-hide. Same properties + readingSessionId as the final record. A
   *  DIFFERENT name on purpose: every consumer of Article Reading Session
   *  (Newsroom Score, article_read_5m, baselines, reader profiles) keeps
   *  seeing exactly one row per read; the server synthesizes a final from the
   *  last checkpoint only when the real final never arrives (WebView kill). */
  ARTICLE_READING_CHECKPOINT: 'Article Reading Checkpoint',
  ARTICLE_SCROLL_MILESTONE: 'Article Scroll Milestone',
  ARTICLE_SHARED: 'Article Shared',
  COMMENT_POSTED: 'Comment Posted',
  ARTICLE_SAVED: 'Article Saved',
  SEARCH_PERFORMED: 'Search Performed',
} as const;

// =============================================================================
// BASE PROPERTIES BUILDER
// =============================================================================

/**
 * Extract domain from URL
 */
function extractDomain(url: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.hostname;
  } catch {
    return undefined;
  }
}

/**
 * Build base article properties
 */
export function buildBaseProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  /** Wave 2b (§S3): `false` on the events that FOLLOW the article's first one.
   *  Only `url` is dropped, and only under leanProperties — see the note at
   *  the assignment below for why the rest of the metadata has to stay. */
  first = true
): ArticleViewedProperties {
  // Get referrer from document
  const referrer = typeof document !== 'undefined' ? document.referrer : undefined;
  const referrerDomain = referrer ? extractDomain(referrer) : undefined;

  // Only include referrer if it's from a different domain (not internal navigation)
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const isExternalReferrer = referrerDomain && currentDomain && referrerDomain !== currentDomain;

  // `collectTags: 'off'` stops the tags at the source. The server also caps
  // and strips them at ingest (utils/event-tags.ts), but that only saves OUR
  // pipeline — this saves the reader's upload too. M32 / §S4.
  const tags = config.collectTags === 'off' ? undefined : article.tags;

  const base: ArticleViewedProperties = {
    articleId: article.articleId || article.path.replace(/^\//, '').replace(/\/$/, '') || 'unknown',
    title: article.title,
    // §S3: `url` duplicates context.page.url, which is what the worker writes
    // to the page_url column and what four materialized views key their
    // `domain` on — so dropping it from the events after the first costs
    // nothing. The ONE reader is the enrichment enqueue in event.worker.ts,
    // and it runs only on `Article Viewed` and already falls back to pageUrl.
    //
    // ⚠️ `path` is NOT dropped with it, even though it looks identical in
    // shape: it is the fallback source of article_id
    // (`eventProperties.articleId || eventProperties.path`) and 10.1 % of
    // dir.bg's article rows have no articleId at all. Dropping it would empty
    // article_id on those events, which drops them out of every MV that
    // filters article_id != ''.
    //
    // ⚠️ title / author / category / publishedAt are NOT dropped either.
    // analytics-news.ts reads them with argMax(x, timestamp) over a group of
    // MIXED event names, and the LAST event of an article view is almost
    // always Article Reading Session — so an empty value there wins and the
    // News Analytics "Articles" screen would show blank titles for every
    // article. Making that safe needs argMaxIf + group-level filters, which is
    // a separate change (§7в).
    url: (first || !config.leanProperties) ? article.url : undefined,
    path: article.path,
    author: article.author,
    category: article.category,
    publishedAt: article.publishedAt,
    tags,
    wordCount: article.wordCount,
    articleType: article.articleType,
    siteName: config.siteName,
    referrer: isExternalReferrer ? referrer : undefined,
    referrerDomain: isExternalReferrer ? referrerDomain : undefined,
  };

  // Keys with zero readers anywhere in the platform (checked across all seven
  // repos 2026-09-21, §S6). Kept by default so an existing integration that
  // reads them in its own beforeSend is not broken silently; dropped when the
  // Site Profile says leanProperties.
  //
  // `articleType` is deliberately NOT here: nothing reads it either, but
  // news-traits reads `contentType`, which the SDK has never sent — so this is
  // the raw material for fixing that (§S13), not dead weight.
  if (!config.leanProperties) {
    base.section = article.section;
    base.language = article.language;
    base.readingTimeMinutes = article.readingTimeMinutes;
    base.isPremium = article.isPremium;
    base.siteId = config.siteId;
  }
  return base;
}

// =============================================================================
// ARTICLE VIEWED
// =============================================================================

/**
 * Build Article Viewed event properties
 */
export function buildArticleViewedProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig
): ArticleViewedProperties {
  return buildBaseProperties(article, config);
}

// =============================================================================
// ARTICLE ENGAGED
// =============================================================================

/**
 * Build Article Engaged event properties
 */
export function buildArticleEngagedProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  state: ReadingState,
  trigger: 'scroll' | 'time' | 'both'
): ArticleEngagedProperties {
  return {
    ...buildBaseProperties(article, config, false),
    timeOnPageSeconds: Math.round(state.totalTimeMs / 1000),
    scrollDepthPercent: state.maxScrollDepth,
    engageTrigger: trigger,
  };
}

// =============================================================================
// ARTICLE READ
// =============================================================================

/**
 * Build Article Read event properties
 */
export function buildArticleReadProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  state: ReadingState,
  engageTrigger: 'scroll' | 'time' | 'both',
  readTrigger: 'scroll' | 'time' | 'both'
): ArticleReadProperties {
  return {
    ...buildBaseProperties(article, config, false),
    timeOnPageSeconds: Math.round(state.totalTimeMs / 1000),
    scrollDepthPercent: state.maxScrollDepth,
    engageTrigger,
    readTrigger,
  };
}

// =============================================================================
// ARTICLE READING SESSION
// =============================================================================

/**
 * Build Article Reading Session event properties
 */
export function buildArticleReadingSessionProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  state: ReadingState,
  visibleTimeSeconds: number,
  checkpoint = false
): ArticleReadingSessionProperties {
  return {
    ...buildBaseProperties(article, config, false),
    readingSessionId: state.readingSessionId,
    checkpoint,
    checkpointSeq: config.leanProperties ? undefined : state.checkpointSeq,
    sessionDurationSeconds: Math.round(state.totalTimeMs / 1000),
    visibleTimeSeconds,
    maxScrollDepthPercent: state.maxScrollDepth,
    scrollUpCount: state.scrollUpCount,
    // §S6: no reader anywhere. Dropped under leanProperties.
    scrollDownCount: config.leanProperties ? undefined : state.scrollDownCount,
    wasEngaged: state.engagedSent,
    wasRead: state.readSent,
    milestones: Array.from(state.trackedMilestones).sort((a, b) => a - b),
    // Per-milestone timing — preserves the drop-off + timing that the separate
    // Article Scroll Milestone events carried, so lean mode loses no data.
    milestoneTimings: state.milestoneTimings,
  };
}

// =============================================================================
// ARTICLE SCROLL MILESTONE
// =============================================================================

/**
 * Build Article Scroll Milestone event properties
 */
export function buildArticleScrollMilestoneProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  milestone: number,
  timeToMilestoneSeconds: number
): ArticleScrollMilestoneProperties {
  return {
    ...buildBaseProperties(article, config, false),
    milestone,
    timeToMilestoneSeconds,
  };
}

// =============================================================================
// ARTICLE SHARED
// =============================================================================

/**
 * Build Article Shared event properties
 */
export function buildArticleSharedProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  shareMethod?: string,
  shareDestination?: string
): ArticleSharedProperties {
  return {
    ...buildBaseProperties(article, config),
    shareMethod,
    shareDestination,
  };
}

// =============================================================================
// SHARE DETECTION
// =============================================================================

/**
 * Detect share destination from element or URL
 */
export function detectShareDestination(
  element: Element | null,
  href?: string
): string {
  // Check element classes and data attributes
  if (element) {
    const classNames = element.className.toLowerCase();
    const dataShare = element.getAttribute('data-share')?.toLowerCase();

    if (dataShare) return dataShare;

    if (classNames.includes('facebook') || classNames.includes('fb')) return 'facebook';
    if (classNames.includes('twitter') || classNames.includes('-x')) return 'twitter';
    if (classNames.includes('linkedin')) return 'linkedin';
    if (classNames.includes('email')) return 'email';
    if (classNames.includes('whatsapp')) return 'whatsapp';
    if (classNames.includes('telegram')) return 'telegram';
    if (classNames.includes('copy')) return 'copy';
    if (classNames.includes('pinterest')) return 'pinterest';
    if (classNames.includes('reddit')) return 'reddit';
  }

  // Check href
  const url = href || (element as HTMLAnchorElement)?.href || '';
  if (url) {
    if (url.includes('facebook.com')) return 'facebook';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter';
    if (url.includes('linkedin.com')) return 'linkedin';
    if (url.includes('mailto:')) return 'email';
    if (url.includes('whatsapp.com') || url.includes('wa.me')) return 'whatsapp';
    if (url.includes('telegram.me') || url.includes('t.me')) return 'telegram';
    if (url.includes('pinterest.com')) return 'pinterest';
    if (url.includes('reddit.com')) return 'reddit';
  }

  return 'unknown';
}

// =============================================================================
// COMMENT POSTED
// =============================================================================

/**
 * Build Comment Posted event properties
 */
export function buildCommentPostedProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  commentId?: string,
  parentCommentId?: string,
  commentLength?: number
): CommentPostedProperties {
  return {
    ...buildBaseProperties(article, config),
    commentId,
    parentCommentId,
    commentLength,
  };
}

// =============================================================================
// ARTICLE SAVED
// =============================================================================

/**
 * Build Article Saved event properties
 */
export function buildArticleSavedProperties(
  article: ArticleMetadata,
  config: NewsPluginConfig,
  saveLocation?: string
): ArticleSavedProperties {
  return {
    ...buildBaseProperties(article, config),
    saveLocation,
  };
}

// =============================================================================
// SEARCH PERFORMED
// =============================================================================

/**
 * Build Search Performed event properties
 */
export function buildSearchPerformedProperties(
  query: string,
  config: NewsPluginConfig,
  resultsCount?: number,
  searchType?: string,
  filters?: Record<string, string>
): SearchPerformedProperties {
  return {
    query,
    resultsCount,
    searchType,
    filters,
    siteName: config.siteName,
    siteId: config.siteId,
  };
}
